import { useEffect, useState } from "react";
import { fz } from "@/lib/fanzone";
import { toast } from "sonner";
import { Flame, Trophy, Pencil } from "lucide-react";

export const useFanProfile = () => {
  const [profile, setProfile] = useState(null);
  const load = async () => {
    try {
      setProfile((await fz.hello()).data);
    } catch { /* silent */ }
  };
  useEffect(() => {
    load();
    window.addEventListener("fz:refresh", load);
    return () => window.removeEventListener("fz:refresh", load);
  }, []);
  return profile;
};

export default function ProfileBar({ profile }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  if (!profile) {
    return <div className="titans-card p-5 text-titans-silver text-sm" data-testid="fz-profile-loading">Loading your profile…</div>;
  }

  const span = Math.max(1, profile.level_next - profile.level_floor);
  const pct = Math.round(((profile.xp - profile.level_floor) / span) * 100);

  const saveName = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      await fz.setName(name.trim());
      toast.success(`Welcome to the leaderboard, ${name.trim()}!`);
      setEditing(false);
      window.dispatchEvent(new Event("fz:refresh"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save name");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="titans-card p-5 md:p-6" data-testid="fz-profile-bar">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="shrink-0 w-12 h-12 rounded-full bg-titans-navy text-white flex items-center justify-center font-display text-xl">
            {profile.level}
          </div>
          <div className="min-w-0">
            {editing ? (
              <form onSubmit={saveName} className="flex gap-2">
                <input
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={20}
                  placeholder="Pick a fan name…"
                  data-testid="fz-name-input"
                  className="bg-titans-void border border-[rgba(138,141,143,0.3)] focus:border-titans-blue focus:outline-none px-3 py-1.5 font-body text-sm w-44"
                />
                <button type="submit" disabled={saving} data-testid="fz-name-save" className="bg-titans-red text-white px-3 py-1.5 font-cond uppercase tracking-wider text-xs disabled:opacity-50">
                  Save
                </button>
              </form>
            ) : (
              <button onClick={() => { setEditing(true); setName(profile.name || ""); }} data-testid="fz-name-btn" className="flex items-center gap-1.5 font-display text-xl uppercase leading-none truncate">
                {profile.name || "Claim your name"}
                <Pencil size={12} className="text-titans-silver shrink-0" />
              </button>
            )}
            <div className="font-cond uppercase tracking-wider text-[10px] text-titans-blue mt-1">
              {profile.level_title} · Rank #{profile.rank}
            </div>
          </div>
        </div>

        <div className="flex-1 min-w-[180px]">
          <div className="flex justify-between font-cond uppercase tracking-wider text-[10px] text-titans-silver mb-1">
            <span data-testid="fz-xp-label">{profile.xp} XP</span>
            <span>Next level: {profile.level_next}</span>
          </div>
          <div className="h-2.5 rounded-full overflow-hidden" style={{ background: "#E2E8F0" }}>
            <div className="h-full bar-animate rounded-full" style={{ width: `${pct}%`, background: "linear-gradient(90deg, #0C2340, #4B92DB)" }} />
          </div>
        </div>

        <div className="flex items-center gap-5 shrink-0">
          <div className="text-center">
            <div className="flex items-center gap-1 justify-center text-titans-red">
              <Flame size={16} className={profile.streak > 0 ? "" : "opacity-40"} />
              <span className="font-display text-2xl leading-none" data-testid="fz-streak">{profile.streak}</span>
            </div>
            <div className="font-cond uppercase tracking-wider text-[9px] text-titans-silver mt-0.5">Day streak</div>
          </div>
          <div className="text-center">
            <div className="flex items-center gap-1 justify-center text-titans-blue">
              <Trophy size={15} />
              <span className="font-display text-2xl leading-none">{(profile.achievements || []).length}</span>
            </div>
            <div className="font-cond uppercase tracking-wider text-[9px] text-titans-silver mt-0.5">Badges</div>
          </div>
        </div>
      </div>
    </div>
  );
}
